import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactDOM from "react-dom";

const DropdownPortal = ({ isOpen, buttonRef, children }) => {
  if (!isOpen || !buttonRef) return null;

  const rect = buttonRef.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const dropdownWidth = Math.min(rect.width, viewportWidth - 32);

  return ReactDOM.createPortal(
    <div
      className="absolute z-[9999]"
      style={{
        top: rect.bottom + window.scrollY + 6,
        left: Math.max(16, rect.left + window.scrollX),
        width: dropdownWidth,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

const CustomTdDropdown = ({
  idx,
  value,
  options = [],
  onSelect,
  openDropdown,
  setOpenDropdown,
  placeholder = "Select...",
  searchable = false,
  maxVisibleItems = 5,
  disabled,
  parentClass,
}) => {
  const [search, setSearch] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const buttonRef = useRef(null);
  const listRefRev = useRef(null);

  // filter options on search
  useEffect(() => {
    if (searchable) {
      setFilteredOptions(
        options.filter((opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase()),
        ),
      );
    } else {
      setFilteredOptions(options);
    }
  }, [search, options, searchable]);

  // close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        listRefRev.current &&
        !listRefRev.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpenDropdown]);

  const rowHeight = 40;
  const maxHeight = rowHeight * maxVisibleItems;

  return (
    <div className="relative">
      {/* Button */}
      <button
        ref={buttonRef}
        disabled={disabled}
        onClick={() => setOpenDropdown(openDropdown === idx ? null : idx)}
        className={`${parentClass ? parentClass : "border-gray-300"} 
w-full h-10 rounded-xl text-sm px-3 flex justify-between items-center border bg-white
        ${
          disabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
            : "bg-white text-black cursor-pointer"
        }`}
      >
        <span className="truncate">
          {value?.label || value?.value || placeholder}
        </span>
        <img
          src={openDropdown === idx ? "/Vector.svg" : "/down-grey.svg"}
          className={`w-3 h-3 transition-transform duration-200 ${
            openDropdown === idx ? "rotate-180" : ""
          }`}
          alt=""
        />
      </button>

      {/* Dropdown rendered in portal */}
      <DropdownPortal
        isOpen={openDropdown === idx}
        buttonRef={buttonRef.current}
      >
        <motion.div
          ref={listRefRev}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="bg-white border rounded-xl shadow-lg overflow-y-auto"
          style={{ maxHeight: `${maxHeight}px` }}
        >
          {searchable && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 text-sm border-b focus:outline-none"
            />
          )}

          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => {
                  console.log("revenueType in td" + option.label);
                  onSelect(option);
                  setOpenDropdown(null);
                }}
                className="px-4 py-2 cursor-pointer text-sm hover:bg-black hover:text-white rounded-md"
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className="px-4 py-2 text-gray-500 text-sm">No options</div>
          )}
        </motion.div>
      </DropdownPortal>
    </div>
  );
};

export default CustomTdDropdown;
